/**
 * Which area does a client system's un-addressed server send belong to?
 *
 * A client can legitimately be joined to several areas at once — a global
 * `friends-list` area plus a gameplay area, say — and a bare
 * `this.dispatchToServer(...)` used to fan out to ALL of them. It now goes to
 * the one area that started the system: area files register their systems
 * through `Process.addSystemsArrayFromFile(systems, areaType)`, which records
 * the owning area type in `Process.systemOwners`.
 *
 * The resolution lives here — not inside `ClientProcess` — so the real client,
 * the in-process test harness and the mock server initializer all route through
 * the SAME code and cannot drift apart.
 */

import { GAME_FILE_SYSTEM_OWNER } from "@gotti/core";

/** The slice of a client process the routing rules need. */
export type AreaRoutingProcess = {
  joinedAreasById: { [areaId: string]: any };
  startedAreas: { [areaType: string]: { ids: Array<string | number> } };
  systemOwners: { [systemName: string | number]: Set<string> };
  /** Areas mid-departure — see `departingAreaIds` on ClientProcess. */
  departingAreaIds?: Set<string>;
  /** Local games have no areas to route to; see `resolveAreaForSystem`. */
  isNetworked?: boolean;
};

/**
 * Every joined areaId this system could be dispatching to, derived from the
 * area file(s) that added it. A game-file system is owned by no area, so every
 * joined area is a candidate. Ids come back in `joinedAreasById` key form
 * (strings) — that is what travels on the wire.
 *
 * Areas the client is in the middle of LEAVING are dropped: an area switch adds
 * the incoming area before removing the outgoing one, so for the whole window —
 * `initAreaFile`, `onStart`, `onJoinedArea` — both are joined and a dispatch
 * would otherwise look ambiguous. If dropping them would leave nothing, they
 * stay: the mark exists to disambiguate, never to break a system whose only
 * area is on its way out (a goodbye message from `onLeftArea` still sends).
 */
export function getAreaIdsForSystem(process: AreaRoutingProcess, systemName: string | number): string[] {
  const joinedIds = Object.keys(process.joinedAreasById ?? {});
  const owners = process.systemOwners?.[systemName];
  const candidates = new Set<string>();
  if (!owners || owners.size === 0 || owners.has(GAME_FILE_SYSTEM_OWNER)) {
    joinedIds.forEach(id => candidates.add(id));
  }
  owners?.forEach(owner => {
    if (owner === GAME_FILE_SYSTEM_OWNER) return;
    process.startedAreas?.[owner]?.ids.forEach(id => {
      const key = String(id);
      if (key in (process.joinedAreasById ?? {})) candidates.add(key);
    });
  });
  const all = Array.from(candidates);
  const departing = process.departingAreaIds;
  if (!departing?.size) return all;
  const staying = all.filter(id => !departing.has(id));
  return staying.length ? staying : all;
}

/**
 * The single area a system's server send routes to.
 *
 * - exactly one candidate → that areaId.
 * - several → THROW. Two joined instances of the owning area type, two area
 *   files that both added the system, or a game-file system while several areas
 *   are joined: the framework will not guess, the caller must name the area
 *   (`dispatchToArea(areaId, message)` / `requestArea({ areaId, ... })`).
 * - none → THROW. There is no un-addressed fallback: a server send that belongs
 *   to no area is an error, not a silent fan-out to whatever the client happens
 *   to be in. An area-file system can only be running because its area is
 *   joined, so the one way to reach this is a GAME-file system dispatching
 *   before the first area join lands (`startGame` runs, and therefore `onStart`
 *   fires, before `joinInitialAreas`) — hence the "dispatch from onJoinedArea
 *   onward" hint in the message.
 *
 * Returns `undefined` — routing skipped entirely, caller sends un-addressed as
 * it always did — only for a process that is not networked. That covers local
 * games and the in-process unit-test harnesses, which add client systems to a
 * bare process with no game file, no area file and no joins.
 */
export function resolveAreaForSystem(
  process: AreaRoutingProcess,
  systemName: string | number,
): string | undefined {
  if (!process.isNetworked) return undefined;
  const candidates = getAreaIdsForSystem(process, systemName);
  if (candidates.length === 1) return candidates[0];
  if (candidates.length === 0) {
    throw new Error(
      `System "${String(systemName)}" tried to send to the server, but the client is not joined to any area. ` +
      `A server send is always addressed to one area — dispatch from onJoinedArea onward (a game-file ` +
      `system's onStart runs before the first area join), or name the area with dispatchToArea(areaId, message).`,
    );
  }
  const owners = Array.from(process.systemOwners?.[systemName] ?? []);
  const areaOwners = owners.filter(o => o !== GAME_FILE_SYSTEM_OWNER);
  const ownerDesc = areaOwners.length
    ? `the area file(s): ${areaOwners.join(', ')}`
    : `the game file, so it belongs to no single area`;
  throw new Error(
    `System "${String(systemName)}" can not resolve which area to send to. It was added by ${ownerDesc}, ` +
    `and the client is joined to ${candidates.length} matching areas (${candidates.join(', ')}). ` +
    `Name the area explicitly: dispatchToArea(areaId, message) / requestArea({ areaId, ... }).`,
  );
}
